import { useEffect, useMemo, useState } from "react";
import { Phone, Search, Users } from "lucide-react";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { EmptyState, ErrorState, Loading, PageHeader, Surface } from "@/components/ds";
import { Input } from "@/components/ui/input";
import { CustomerDetailModal } from "@/components/customers/CustomerDetailModal";
import { CustomerRanking } from "@/components/customers/CustomerRanking";
import { Button } from "@/components/ui/button";

export default function Customers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("LISTA");

  const load = async () => {
    setError("");
    try {
      const { data: res } = await api.get("/customers");
      setData(res);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = data?.customers || [];
    if (!term) return list;
    return list.filter((c) =>
      [c.name, c.phone, c.phone_key].filter(Boolean).some((v) => v.toLowerCase().includes(term))
    );
  }, [data, search]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando clientes..." />;

  const sold = data.customers.reduce((acc, c) => acc + (c.sold_total || 0), 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <PageHeader
        title="Clientes"
        subtitle={
          data.customers.length === 0
            ? "Os clientes aparecem aqui automaticamente conforme os pedidos chegam."
            : `${data.customers.length} cliente(s) · ${brl(sold)} em vendas efetivadas`
        }
      />

      <div className="flex flex-wrap gap-2" data-testid="customers-tabs">
        {[
          { key: "LISTA", label: "Lista de clientes" },
          { key: "RANKING", label: "Ranking de compradores" },
        ].map((t) => (
          <Button
            key={t.key}
            size="sm"
            variant={tab === t.key ? "default" : "outline"}
            onClick={() => setTab(t.key)}
            className="rounded-full"
            data-testid={`customers-tab-${t.key.toLowerCase()}`}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === "RANKING" ? (
        <CustomerRanking onOpen={setOpenId} />
      ) : (
        <>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou telefone"
          data-testid="customer-search-input"
          className="pl-9"
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nenhum cliente por aqui"
          description="Cada pedido do Catálogo Online ou do + Pedido cria ou reaproveita um cadastro automaticamente. Parceiros continuam na própria área."
        />
      ) : (
        <Surface className="overflow-x-auto p-0" data-testid="customers-list">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Pedidos</th>
                <th className="px-4 py-3 font-medium">Primeiro</th>
                <th className="px-4 py-3 font-medium">Último</th>
                <th className="px-4 py-3 text-right font-medium">Em pedidos</th>
                <th className="px-4 py-3 text-right font-medium">Vendido</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setOpenId(c.id)}
                  data-testid={`customer-row-${c.id}`}
                  className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-secondary/40"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{c.name || "Sem nome"}</p>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" /> {c.phone || c.phone_key}
                    </p>
                  </td>
                  <td className="px-4 py-3 font-num text-xs">
                    {c.orders_count}
                    {c.cancelled_count > 0 && (
                      <span className="text-muted-foreground"> · {c.cancelled_count} canc.</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                    {c.first_order_at ? dataBR(c.first_order_at) : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                    {c.last_order_at ? dataBR(c.last_order_at) : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-num text-xs text-muted-foreground">
                    {brl(c.orders_total)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-num text-xs">
                    {brl(c.sold_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      )}
        </>
      )}

      {openId && (
        <CustomerDetailModal
          customerId={openId}
          customers={data.customers}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
